import React, { Children } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import Icon from 'dpl/common/components/Icon';
import useFormContext from 'dpl/shared/form/hooks/useFormContext';

import Label from './Label';

// TODO: multiple a11y labeling with descendant form fields
export default function MultiFieldGroup({
  allowNone,
  children,
  includeCount,
  label,
  name,
  shortLabel,
  sectionDivider,
  maxAllowed
}) {
  const formContext = useFormContext(name);
  const pushToFormState = formContext.pushToFormState || formContext.pushValue;
  const removeFromFormState =
    formContext.removeFromFormState || formContext.removeValue;

  let displayIndex = 0;

  return (
    <div className="MultiFieldGroup">
      {Children.map(children, (child, idx) => {
        if (!child) return null;

        displayIndex++;

        const childName = `${name}.${idx}`;
        const firstValidIndex = children.findIndex(c => c);
        // 0 may not be the first rendered child in a form state
        // so we need to find the index of the first rendered element

        return (
          <div
            data-test-id={`MultiFieldGroup__child--${idx}`}
            key={idx}
            className={classnames('mb1', {
              'pt4 bt b--light-gray': sectionDivider && idx !== firstValidIndex
            })}
          >
            <div className="flex">
              <div className="flex-auto">
                {label && (
                  <Label
                    text={includeCount ? `${label} ${displayIndex}` : label}
                  />
                )}
              </div>
              {(allowNone || idx !== firstValidIndex) && (
                <button
                  className="f2 link--primary"
                  data-test-id={childName}
                  onClick={() => removeFromFormState(childName)}
                  type="button"
                >
                  Remove
                </button>
              )}
            </div>
            {child}
          </div>
        );
      })}
      {Children.toArray(children).length < maxAllowed && (
        <button
          type="button"
          className="pt2 inline-flex align-items-center link--primary"
          onClick={() => pushToFormState(name)}
        >
          <Icon
            className="ba bw1 br-100"
            height="16px"
            width="16px"
            name="plus"
          />
          <span>
            &nbsp;Add {shortLabel || `another ${label.toLowerCase()}`}
          </span>
        </button>
      )}
    </div>
  );
}

MultiFieldGroup.propTypes = {
  allowNone: PropTypes.bool,
  children: PropTypes.node,
  includeCount: PropTypes.bool,
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  shortLabel: PropTypes.string,
  sectionDivider: PropTypes.bool,
  maxAllowed: PropTypes.number
};

MultiFieldGroup.defaultProps = {
  allowNone: false,
  children: null,
  includeCount: false,
  label: null,
  shortLabel: null,
  sectionDivider: false,
  maxAllowed: Infinity
};
